/**
 * e-Tesa — Background Jobs Monitor Panel
 * Vedere unificată live pentru task-uri async din backend:
 *  - Trimitere contract email
 *  - Extragere antemăsurătoare AI
 * Cu contoare, filtrare status, auto-refresh 5s, marcare stalled jobs.
 */
import { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Activity, RefreshCw, Mail, Sparkles, CheckCircle2, XCircle, Loader2,
  AlertTriangle, ChevronDown, ChevronUp,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABELS = {
  processing: { label: "În procesare", color: "bg-blue-100 text-blue-700 border-blue-200", icon: Loader2 },
  sent: { label: "Reușit", color: "bg-emerald-100 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
  failed: { label: "Eșuat", color: "bg-rose-100 text-rose-700 border-rose-200", icon: XCircle },
};

const TYPE_META = {
  contract_email: { icon: Mail, color: "text-teal-600", bg: "bg-teal-50" },
  devizpro_extract: { icon: Sparkles, color: "text-amber-600", bg: "bg-amber-50" },
};

function formatDateTime(iso) {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    return d.toLocaleString("ro-RO", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch { return iso; }
}

function formatDuration(s) {
  if (s == null) return "-";
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = Math.round(s % 60);
  return `${m}m ${rem}s`;
}

function formatBytes(n) {
  if (!n) return "-";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function JobRow({ job, onMarkStalled }) {
  const [expanded, setExpanded] = useState(false);
  const typeMeta = TYPE_META[job.type] || {};
  const TypeIcon = typeMeta.icon || Activity;
  const statusMeta = STATUS_LABELS[job.status] || { label: job.status, color: "bg-slate-100 text-slate-700 border-slate-200", icon: Activity };
  const StatusIcon = statusMeta.icon;

  return (
    <div className="border border-slate-200 rounded-lg p-3 hover:shadow-sm transition-shadow bg-white" data-testid={`job-row-${job.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${typeMeta.bg}`}>
            <TypeIcon className={`w-5 h-5 ${typeMeta.color}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-sm text-slate-800">{job.type_label}</span>
              <Badge className={`${statusMeta.color} border text-xs font-medium py-0.5 flex items-center gap-1`}>
                <StatusIcon className={`w-3 h-3 ${job.status === "processing" ? "animate-spin" : ""}`} />
                {statusMeta.label}
              </Badge>
            </div>
            <div className="text-xs text-slate-500 mt-1 flex items-center gap-3 flex-wrap">
              <span>Creat: {formatDateTime(job.created_at)}</span>
              <span>· Durată: <span className="font-medium text-slate-700">{formatDuration(job.duration_s)}</span></span>
              {job.meta?.num_files ? <span>· {job.meta.num_files} fișiere</span> : null}
              {job.meta?.total_bytes ? <span>· {formatBytes(job.meta.total_bytes)}</span> : null}
            </div>
            {/* Subtitlu specific tipului */}
            {job.type === "contract_email" && (
              <div className="text-xs text-slate-600 mt-1 truncate">
                Către: <span className="font-medium">{job.meta?.recipient_email}</span>
                {job.meta?.contract_number ? <> · Contract {job.meta.contract_number}</> : null}
              </div>
            )}
            {job.type === "devizpro_extract" && job.meta?.activities_extracted != null && (
              <div className="text-xs text-slate-600 mt-1">
                <span className="font-medium">{job.meta.activities_extracted}</span> activități extrase
              </div>
            )}
            {job.error && (
              <div className="text-xs text-rose-600 mt-1 flex items-start gap-1">
                <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                <span className="break-words">{job.error}</span>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {job.status === "processing" && job.duration_s != null && job.duration_s > 300 && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onMarkStalled(job)}
              className="text-rose-600 border-rose-200 hover:bg-rose-50 h-7 text-xs"
              data-testid={`mark-stalled-${job.id}`}
              title="Marchează ca blocat (worker nu răspunde)"
            >
              Marchează blocat
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setExpanded((v) => !v)}
            className="h-7 w-7 p-0"
            title={expanded ? "Ascunde detalii" : "Arată detalii"}
          >
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
        </div>
      </div>
      {expanded && (
        <div className="mt-3 pt-3 border-t border-slate-100 text-xs space-y-1">
          <div className="text-slate-500">
            <span className="font-medium text-slate-700">ID:</span> <code className="text-[10px] bg-slate-100 px-1 py-0.5 rounded">{job.id}</code>
          </div>
          <div className="text-slate-500">
            <span className="font-medium text-slate-700">Ultimul update:</span> {formatDateTime(job.updated_at)}
          </div>
          {job.meta && (
            <div className="text-slate-500">
              <span className="font-medium text-slate-700">Detalii:</span>
              <pre className="mt-1 bg-slate-50 p-2 rounded text-[10px] overflow-x-auto">{JSON.stringify(job.meta, null, 2)}</pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function BackgroundJobsPanel({ open, onOpenChange, companyId }) {
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState({ processing: 0, sent_today: 0, failed_last_24h: 0 });
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchJobs = useCallback(async () => {
    if (!companyId || !open) return;
    setLoading(true);
    try {
      const params = { company_id: companyId, limit: 100 };
      if (statusFilter !== "all") params.status = statusFilter;
      if (typeFilter !== "all") params.job_type = typeFilter;
      const [jobsRes, statsRes] = await Promise.all([
        axios.get(`${API}/background-jobs`, { params }),
        axios.get(`${API}/background-jobs/stats`, { params: { company_id: companyId } }),
      ]);
      setJobs(Array.isArray(jobsRes.data) ? jobsRes.data : []);
      setStats(statsRes.data || { processing: 0, sent_today: 0, failed_last_24h: 0 });
      setLastRefresh(new Date());
    } catch (err) {
      console.error("Failed to load jobs:", err);
    } finally {
      setLoading(false);
    }
  }, [companyId, statusFilter, typeFilter, open]);

  // Auto-refresh la 5s cât timp e deschis
  useEffect(() => {
    if (!open) return;
    fetchJobs();
    const interval = setInterval(fetchJobs, 5000);
    return () => clearInterval(interval);
  }, [open, fetchJobs]);

  const handleMarkStalled = useCallback(async (job) => {
    if (!window.confirm(`Marchezi job-ul "${job.type_label}" ca blocat? Va apărea ca "eșuat" în listă.`)) return;
    try {
      await axios.post(`${API}/background-jobs/${job.type}/${job.id}/retry`);
      toast.success("Job marcat ca eșuat. Reîncearcă operațiunea originală.");
      fetchJobs();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Nu s-a putut marca job-ul");
    }
  }, [fetchJobs]);

  const filteredJobs = useMemo(() => jobs, [jobs]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-teal-700">
            <Activity className="w-5 h-5" />
            Jobs în procesare
            <span className="text-xs text-slate-500 font-normal ml-auto">
              {lastRefresh ? `Actualizat: ${lastRefresh.toLocaleTimeString("ro-RO")}` : ""}
            </span>
          </DialogTitle>
        </DialogHeader>

        {/* Contoare live */}
        <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-3">
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
            <div className="flex items-center gap-2 text-blue-700">
              <Loader2 className={`w-4 h-4 ${stats.processing > 0 ? "animate-spin" : ""}`} />
              <span className="text-xs font-medium">În procesare</span>
            </div>
            <div className="text-2xl font-bold text-blue-800 mt-1" data-testid="stat-processing">{stats.processing}</div>
          </div>
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
            <div className="flex items-center gap-2 text-emerald-700">
              <CheckCircle2 className="w-4 h-4" />
              <span className="text-xs font-medium">Reușite azi</span>
            </div>
            <div className="text-2xl font-bold text-emerald-800 mt-1" data-testid="stat-sent-today">{stats.sent_today}</div>
          </div>
          <div className="rounded-lg border border-rose-200 bg-rose-50 p-3">
            <div className="flex items-center gap-2 text-rose-700">
              <XCircle className="w-4 h-4" />
              <span className="text-xs font-medium">Eșuate (24h)</span>
            </div>
            <div className="text-2xl font-bold text-rose-800 mt-1" data-testid="stat-failed-24h">{stats.failed_last_24h}</div>
          </div>
        </div>

        {/* Filtre */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <div className="flex items-center gap-1">
            <span className="text-xs text-slate-500 mr-1">Status:</span>
            {["all", "processing", "sent", "failed"].map((s) => (
              <Button
                key={s}
                size="sm"
                variant={statusFilter === s ? "default" : "outline"}
                onClick={() => setStatusFilter(s)}
                className="h-7 text-xs px-2"
                data-testid={`filter-status-${s}`}
              >
                {s === "all" ? "Toate" : STATUS_LABELS[s]?.label || s}
              </Button>
            ))}
          </div>
          <div className="flex items-center gap-1">
            <span className="text-xs text-slate-500 mr-1">Tip:</span>
            {[
              { v: "all", l: "Toate" },
              { v: "contract_email", l: "Email contract" },
              { v: "devizpro_extract", l: "Antemăsurători" },
            ].map((t) => (
              <Button
                key={t.v}
                size="sm"
                variant={typeFilter === t.v ? "default" : "outline"}
                onClick={() => setTypeFilter(t.v)}
                className="h-7 text-xs px-2"
                data-testid={`filter-type-${t.v}`}
              >
                {t.l}
              </Button>
            ))}
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={fetchJobs}
            className="h-7 text-xs ml-auto"
            disabled={loading}
            data-testid="refresh-jobs"
          >
            <RefreshCw className={`w-3 h-3 mr-1 ${loading ? "animate-spin" : ""}`} />
            Reîmprospătează
          </Button>
        </div>

        {/* Listă job-uri */}
        <div className="flex-1 overflow-y-auto space-y-2 pr-1" data-testid="jobs-list">
          {filteredJobs.length === 0 ? (
            <div className="text-center text-slate-400 py-12 text-sm">
              {loading ? "Se încarcă..." : "Niciun job de afișat."}
            </div>
          ) : (
            filteredJobs.map((job) => (
              <JobRow key={`${job.type}-${job.id}`} job={job} onMarkStalled={handleMarkStalled} />
            ))
          )}
        </div>

        <div className="text-[10px] text-slate-400 text-center pt-2 border-t border-slate-100">
          Actualizare automată la 5 secunde • Job-urile &quot;în procesare&quot; mai vechi de 5 min pot fi marcate blocate manual
        </div>
      </DialogContent>
    </Dialog>
  );
}
